import { Link } from '@tanstack/react-router'
import { Check, ChevronDown, ChevronRight, Settings } from 'lucide-react'
import type { MailAccount } from '../../lib/api'
import {
  accountTone,
  MAIL_SCOPE_ALL,
  type MailScope,
  sortMailAccounts,
} from '../../lib/mail-account-scope'
import { cn } from '../../lib/utils'
import { Dialog } from '../ui/dialog'
import { AccountDots, AccountLock, AccountTile } from './account-badge'

type SwitcherAccount = Pick<
  MailAccount,
  'id' | 'label' | 'email' | 'protected' | 'createdAt' | 'disabledAt'
>

export interface MailUnread {
  count: number
  byAccount: Record<string, number>
}

/** Konto-Knopf der Bottom-Bar (#535, Mockup 1b g): ersetzt „Sync". Bei „Alle"
 *  drei Firmenpunkte, sonst die Kürzel-Kachel; darunter der Name mit ⌄ und
 *  oben die Ungelesen-Blase (Summe bzw. Zahl des Kontos). Gleiche Maße wie
 *  BarSlot (flex-1 in der h-14-Zeile, also 56 px hoch). */
export function AccountScopeSlot({
  scope,
  accounts,
  unread,
  open,
  onClick,
}: {
  scope: MailScope
  accounts: readonly SwitcherAccount[]
  unread: MailUnread | undefined
  open: boolean
  onClick: () => void
}) {
  const account = scope === MAIL_SCOPE_ALL ? undefined : accounts.find((a) => a.id === scope)
  const label = account?.label ?? 'Alle'
  const count = account ? (unread?.byAccount[account.id] ?? 0) : (unread?.count ?? 0)
  const tones = sortMailAccounts(accounts).map((a) => accountTone(accounts, a.id))
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Konto wählen: ${account ? label : 'Alle Posteingänge'}`}
      aria-haspopup="dialog"
      aria-expanded={open}
      className="relative flex min-w-0 flex-1 flex-col items-center justify-center gap-1 px-1 text-accent transition-colors touch:opacity-100"
    >
      <span className="flex h-5 items-center">
        {account ? (
          <AccountTile label={account.label} tone={accountTone(accounts, account.id)} size="sm" />
        ) : (
          <AccountDots tones={tones.length > 0 ? tones : ['accent', 'info', 'success']} />
        )}
      </span>
      <span className="flex max-w-full items-center gap-0.5 text-2xs font-medium leading-none">
        <span className="truncate">{label}</span>
        <ChevronDown className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />
      </span>
      {count > 0 && (
        <span className="absolute left-[calc(50%+8px)] top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-accent px-1 text-[9px] font-bold leading-none text-bg-base tabular-nums">
          {count > 99 ? '99+' : count}
        </span>
      )}
    </button>
  )
}

/** Sheet „Konto wählen" (#535, Mockup `#s2`): oben der Sammelposteingang,
 *  darunter je Konto Kachel, Name, Adresse, Ungelesen und Schloss; unten der
 *  Weg zur vorhandenen Kontenseite. Jede Zeile ist mindestens 52 px hoch. */
export function AccountSwitcherSheet({
  open,
  scope,
  accounts,
  unread,
  onClose,
  onPick,
}: {
  open: boolean
  scope: MailScope
  accounts: readonly SwitcherAccount[]
  unread: MailUnread | undefined
  onClose: () => void
  onPick: (scope: MailScope) => void
}) {
  const sorted = sortMailAccounts(accounts)
  const allActive = scope === MAIL_SCOPE_ALL
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Konto wählen"
      description="Tippen wechselt · Neue Mail geht aus dem gewählten Konto"
    >
      <div className="flex flex-col gap-1 pt-1">
        <button
          type="button"
          aria-pressed={allActive}
          onClick={() => onPick(MAIL_SCOPE_ALL)}
          className={cn(
            'flex min-h-[52px] items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors',
            allActive ? 'bg-accent/15 text-accent' : 'hover:bg-bg-overlay',
          )}
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-bg-overlay">
            <AccountDots tones={sorted.map((a) => accountTone(accounts, a.id))} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium">Alle Posteingänge</span>
            <span
              className={cn(
                'block truncate text-xs',
                allActive ? 'text-accent/80' : 'text-text-tertiary',
              )}
            >
              Sammelposteingang · {accounts.length} {accounts.length === 1 ? 'Konto' : 'Konten'}
            </span>
          </span>
          {(unread?.count ?? 0) > 0 && (
            <span className="text-sm font-medium tabular-nums text-accent">{unread?.count}</span>
          )}
          {allActive && <Check className="h-4 w-4 shrink-0" aria-hidden="true" />}
        </button>
        <div className="my-1 h-px bg-border-subtle" />
        {sorted.map((account) => {
          const active = scope === account.id
          const count = unread?.byAccount[account.id] ?? 0
          return (
            <button
              key={account.id}
              type="button"
              aria-pressed={active}
              onClick={() => onPick(account.id)}
              className={cn(
                'flex min-h-[52px] items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors',
                active ? 'bg-accent/15' : 'hover:bg-bg-overlay',
              )}
            >
              <AccountTile label={account.label} tone={accountTone(accounts, account.id)} />
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5 text-sm font-medium text-text-primary">
                  <span className="truncate">{account.label}</span>
                  {account.protected && <AccountLock />}
                </span>
                <span className="block truncate text-xs text-text-tertiary">
                  {account.email}
                  {account.disabledAt ? ' · deaktiviert' : ''}
                </span>
              </span>
              {count > 0 && (
                <span className="text-sm font-medium tabular-nums text-accent">{count}</span>
              )}
              {active && <Check className="h-4 w-4 shrink-0 text-accent" aria-hidden="true" />}
            </button>
          )
        })}
        <div className="my-1 h-px bg-border-subtle" />
        <Link
          to="/settings/mail-accounts"
          onClick={onClose}
          className="flex min-h-[52px] items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors hover:bg-bg-overlay"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center text-text-secondary">
            <Settings className="h-5 w-5" aria-hidden="true" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm text-text-primary">Konten verwalten</span>
            <span className="block truncate text-xs text-text-tertiary">
              Zugänge, Schutz und Push der Konten
            </span>
          </span>
          <ChevronRight className="h-4 w-4 shrink-0 text-text-tertiary" aria-hidden="true" />
        </Link>
      </div>
    </Dialog>
  )
}
